import React, { useState } from "react";
import { Ticket } from "../../../../../models/Ticket";
import EAModal from "../../../../shared/Modal/EAModal";
import { useTranslation } from "react-i18next";
import {
  getFormattedValidToDate,
  getFormattedValidFromDate,
  getDisplayTicketType,
  getUsageSectionData,
  shouldShowReservations,
} from "../../../../../utils/TicketPassHelper";
import "./PassModal.scss";
import EAButton from "../../../../shared/Buttons/EAButton";
import DetailsGrid from "./DetailsGrid";
import DetailsUsageProgress from "./DetailsUsageProgress";
import DetailsHeader from "./DetailsHeader";
import DetailsQR from "./DetailsQR";
import DetailsAlert from "./DetailsAlert";
import DetailsExercises from "./DetailsExercises";
import SingleFutureTicketDateSelector from "../../../../MyAccount/FutureTickets/Other/SingleFutureTicketDateSelector";
import { useDispatch } from "react-redux";
import { setSuccessMessage } from "../../../../../store/alerts/alertsActions";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  onRefreshTickets?: () => void;
  tempNotice?: string; // TEMP_PASS_NOTICES
}

const TicketDetailsModal: React.FC<IProps> = ({ isOpen, onClose, ticket, onRefreshTickets, tempNotice }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openDateSelector, setOpenDateSelector] = useState(false);

  // Sekcja "Wykorzystanie" pokazuje już datę ważności (i ew. liczbę wejść) -
  // gdy się wyświetla, w gridzie nie duplikujemy tych informacji
  const usage = getUsageSectionData(ticket);
  const showValidityDatesInGrid = usage.mode === "none";
  const showReservations = shouldShowReservations(ticket);

  // Funkcja do formatowania daty z timestamp'u
  const formatExerciseDate = (startTime: number): string => {
    if (!startTime) return "-";
    const dateObj = typeof startTime === "number" ? new Date(startTime) : new Date();
    return dateObj.toLocaleDateString("pl-PL");
  };

  // Mapa z danymi do wyświetlenia w gridzie
  const detailsData = [
    {
      key: "numer_biletu",
      value: ticket.trhId || "-",
    },
    {
      key: "typ",
      value: getDisplayTicketType(ticket),
    },
    ...(showValidityDatesInGrid
      ? [
          {
            key: "wazny_od",
            value: getFormattedValidFromDate(ticket),
          },
          {
            key: "wazny_do",
            value: getFormattedValidToDate(ticket),
          },
        ]
      : []),
    // Termin w siatce tylko gdy nie ma osobnej sekcji rezerwacji (unikamy duplikatu)
    ...(!showReservations && ticket.exercises && ticket.exercises.length > 0
      ? [
          {
            key: "nazwa_imprezy",
            value: ticket.exercises[0].name || "-",
          },
          {
            key: "data_imprezy",
            value: formatExerciseDate(ticket.exercises[0].startTime),
          },
        ]
      : []),
  ];

  const handleOpenDateSelector = () => {
    setOpenDateSelector(true);
  };

  const handleCloseDateSelector = () => {
    setOpenDateSelector(false);
    // Alert zostanie automatycznie wyczyszczony przy odmontowaniu komponentu
  };

  const handleChangeDateFinish = () => {
    setOpenDateSelector(false);
    dispatch(setSuccessMessage(t("termin_zostal_pomyslnie_wybrany")));
    onRefreshTickets?.();
    onClose();
  };

  return (
    <>
      <EAModal isOpen={isOpen} title={t("szczegoly_biletu")} onClose={onClose} hideButtons desktopWidth="600px">
        <div className="ticket-details">
          {/* SEKCJA 1: Nazwa biletu + Status */}
          <DetailsHeader pass={ticket} />

          {/* SEKCJA 2: Szczegóły biletu - 2 kolumny */}
          <DetailsGrid detailsData={detailsData} />

          {/* SEKCJA: Wykorzystanie - uniwersalna sekcja z paskiem postępu (wejścia lub ważność) */}
          <DetailsUsageProgress pass={ticket} />

          {/* TEMP_PASS_NOTICES - uwaga do biletu */}
          {/* {tempNotice && (
            <EAAlert type="error" message={tempNotice} compact={false} variant="chip" />
          )} */}

          {/* SEKCJA 3: Alert - jeśli bilet wymaga akcji */}
          <DetailsAlert ticket={ticket} onSelectExercise={handleOpenDateSelector} />

          {/* SEKCJA 4: QR Code */}
          <DetailsQR pass={ticket} />

          {/* SEKCJA 5: Rezerwacje — SELECT / FLEXI (i inne typy wymagające zapisu) */}
          {showReservations && <DetailsExercises pass={ticket} onRefreshTickets={onRefreshTickets} />}

          {/* SEKCJA 6: Przyciski */}
          <div className="ticket-details__actions">
            <EAButton title={t("zamknij")} variant="outlined" onClick={onClose} fullWidth />
          </div>
        </div>
      </EAModal>

      {/* Modal wyboru terminu */}
      <EAModal
        isOpen={openDateSelector}
        title={t("wybor_terminu")}
        onClose={handleCloseDateSelector}
        hideButtons
        desktopWidth="600px"
      >
        <SingleFutureTicketDateSelector ticket={ticket} onChangeDateFinish={handleChangeDateFinish} />
      </EAModal>
    </>
  );
};

export default TicketDetailsModal;
